import { API_BASE_URL, getAuthHeaders, enforceAuthResponse } from '../client';

export interface ProgressAmountPct {
  amount: number;
  percentage: number;
}

export interface CollectionProgressSummary {
  total_due: number;
  nach_cleared: ProgressAmountPct;
  collection_done: ProgressAmountPct;
  total_collection: ProgressAmountPct;
}

export interface DpdBucketRow {
  bucket: string;
  total_due: number;
  due_share_percentage: number;
  nach_cleared: ProgressAmountPct;
  collection_done: ProgressAmountPct;
  total_collection: ProgressAmountPct;
}

export type CollectionStatusKey =
  | 'overdue'
  | 'partially_paid'
  | 'paid';

export interface StatusAllocationMetric {
  count: number;
  amount: number;
  count_percentage: number;
  amount_percentage: number;
}

export interface CollectionStatusMatrix {
  overdue: StatusAllocationMetric;
  partially_paid: StatusAllocationMetric;
  paid: StatusAllocationMetric;
  allocation: StatusAllocationMetric;
}

export interface DpdBucketStatusMatrix extends CollectionStatusMatrix {
  bucket: string;
}

export interface CollectionProgressSelection {
  status?: CollectionStatusKey;
  bucket?: string;
}

export type WorklistType = 'ptp' | 'field_visit';
export type WorklistStatus = 'today' | 'missed' | 'not_set' | 'later';

export interface WorklistCounts {
  today: number;
  missed: number;
  not_set: number;
  later: number;
}

export interface WorklistSelection {
  type: WorklistType;
  status: WorklistStatus;
}

export interface DailyWorklist {
  actioned_tasks: number;
  total_tasks: number;
  ptp: WorklistCounts;
  field_visit: WorklistCounts;
  last_updated_at: string;
}

export interface CollectionProgressResponse {
  as_of_date: string;
  emi_month?: string;
  progress: CollectionProgressSummary;
  dpd_buckets: DpdBucketRow[];
  status_summary: CollectionStatusMatrix;
  status_buckets: DpdBucketStatusMatrix[];
  daily_worklist: DailyWorklist;
}

export interface CollectionProgressFilters {
  emi_month?: string;
  state?: string[];
  branch?: string[];
  tl_name?: string[];
  rm_name?: string[];
  source_tl_name?: string[];
  source_rm_name?: string[];
  dealer?: string[];
  lender?: string[];
  search?: string;
  loan_type?: string[];
  status?: string[];
  dpd_bucket?: string[];
  nach_status?: string[];
  ptp_date_filter?: string[];
  ptp_worklist?: WorklistStatus;
  demand_num?: string[];
  special_case_filter?: string[];
}

const inFlightProgress = new Map<string, Promise<CollectionProgressResponse>>();

export async function fetchCollectionProgress(
  filters: CollectionProgressFilters = {},
): Promise<CollectionProgressResponse> {
  const params = new URLSearchParams();
  if (filters.emi_month) params.append('emi_month', filters.emi_month);
  if (filters.state?.length) params.append('state', filters.state.join(','));
  if (filters.branch?.length) params.append('branch', filters.branch.join(','));
  if (filters.tl_name?.length) params.append('tl_name', filters.tl_name.join(','));
  if (filters.rm_name?.length) params.append('rm_name', filters.rm_name.join(','));
  if (filters.source_tl_name?.length) params.append('source_tl_name', filters.source_tl_name.join(','));
  if (filters.source_rm_name?.length) params.append('source_rm_name', filters.source_rm_name.join(','));
  if (filters.dealer?.length) params.append('dealer', filters.dealer.join(','));
  if (filters.lender?.length) params.append('lender', filters.lender.join(','));
  if (filters.search) params.append('search', filters.search);
  if (filters.loan_type?.length) params.append('loan_type', filters.loan_type.join(','));
  if (filters.status?.length) params.append('status', filters.status.join(','));
  if (filters.dpd_bucket?.length) params.append('dpd_bucket', filters.dpd_bucket.join(','));
  if (filters.nach_status?.length) params.append('nach_status', filters.nach_status.join(','));
  if (filters.ptp_date_filter?.length) params.append('ptp_date_filter', filters.ptp_date_filter.join(','));
  if (filters.ptp_worklist) params.append('ptp_worklist', filters.ptp_worklist);
  if (filters.demand_num?.length) params.append('demand_num', filters.demand_num.join(','));
  if (filters.special_case_filter?.length) params.append('special_case_filter', filters.special_case_filter.join(','));

  const qs = params.toString();
  const url = `${API_BASE_URL}/collection-analytics/progress${qs ? `?${qs}` : ''}`;

  const existing = inFlightProgress.get(url);
  if (existing) return existing;

  const request = (async () => {
    const response = await fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    });
    enforceAuthResponse(response);
    if (!response.ok) {
      throw new Error(`Failed to fetch collection progress: ${response.status}`);
    }
    return response.json();
  })().finally(() => {
    inFlightProgress.delete(url);
  });

  inFlightProgress.set(url, request);
  return request;
}
